/**
 * witnessDraft — what a witness has to be before they can be one.
 *
 * THE DEFECT THIS CLOSES IS A NAME-ONLY WITNESS. `contract_witnesses.phone` and `.address` are
 * both nullable and the create form asked for neither, so a witness saved on a name alone
 * satisfied the picker, satisfied the contract's `witness_id`, and then blocked the send — the
 * witness agreement prints their name, their phone AND their address, and the template cannot
 * render a null. The refusal arrived at the ceremony, days later, naming a merge field rather than
 * a person.
 *
 * ONE TEMPLATE, NOT TWO, SINCE [D-B18]. The witness came off the hire purchase agreement entirely:
 * it prints neither the phone nor the address — nor the witness at all — and the instrument that
 * asks for all three is the one addressed to them. The requirement is unchanged; what changed is
 * which document an admin is told it is for, and being told the wrong one is how a refusal sends
 * somebody to the wrong page.
 *
 * ONE VALIDATION, ONE SENTENCE, ONE SITE. Every surface that creates a witness — the selector on
 * the preparation form today, the picker's inline create panel next — calls `validateWitnessDraft`
 * and prints what it returns. A second copy of this rule is how the two surfaces end up disagreeing
 * about what a witness is.
 *
 * THE NEW FIELD DOES NOT JOIN THAT RULE, AND THAT IS THE POINT. `relationship` comes with the
 * restructured schedule, which states how the witness knows the hirer. It does not block a send, so
 * it may not block a save: the refusal above exists because three named fields PRINT and a null
 * cannot be rendered, and stretching it to cover a field the instrument tolerates blank would turn
 * a guarantee into a form policy.
 *
 * `alt_phone` USED TO STAND BESIDE IT AND IS GONE. It was tolerated blank on the page and asked for
 * nowhere an admin fills in first, so every witness carried NULL and the witness agreement's merge
 * pre-flight held open on `witness.alt_phone` — an outstanding fill-in that named a field the owner
 * had never wanted. Removed body, column, form and merge key together rather than made required.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only, and no `supabase` import may ever
 * appear here (the client touches `localStorage` at module scope and takes the test run with it).
 */

/**
 * The three fields the witness agreement prints, and the one the schedule may.
 *
 * REQUIRED AND OPTIONAL ARE NOT THE SAME SHAPE OF FACT, which is why `relationship` is optional
 * here rather than an empty string with a validator that ignores it. The first three block a send;
 * this one does not, and a witness saved without it is a complete witness. It exists because the
 * restructured schedule states how the witness knows the hirer — worth having, not worth refusing
 * a save over.
 */
export interface WitnessDraft {
    name: string;
    phone: string;
    address: string;
    /** How the witness knows the hirer — `Brother`, `Landlord`. Optional. */
    relationship?: string;
}

/** A witness as the pickers list them. */
export interface WitnessOption {
    id: number;
    name: string;
    phone: string | null;
    address: string | null;
    /**
     * The optional field, NULL both when nothing has been recorded and when the read ran against a
     * server whose migration has not landed — the column is new, and the fetch is a star select for
     * exactly that reason (`witness.ts`).
     */
    relationship: string | null;
    /** How many contracts already name this witness. Zero means free to pick. */
    contractCount: number;
}

/**
 * The standing sentence above the create panel's fields — visible before anything is refused,
 * because a requirement an admin reads first is not a refusal at all.
 */
export const WITNESS_CREATE_REQUIREMENT =
    'A witness cannot be saved on name alone — the witness agreement prints the phone and the ' +
    'address as well.';

/** The same requirement, in the voice it takes once a save has actually been attempted. */
export const WITNESS_CREATE_REFUSAL =
    'Name, phone and address are all required — the witness agreement prints all three.';

/** Empty means empty: whitespace is not a phone number. */
function filled(value: string): boolean {
    return value.trim() !== '';
}

/** A cleared field is stored as NULL, not as `''` — one empty has to mean one thing. */
function emptyToNull(value: string): string | null {
    const text = value.trim();
    return text === '' ? null : text;
}

/** The columns the parties block's commit bar writes. The optional one may be ABSENT. */
export interface WitnessContactPatch {
    phone: string | null;
    address: string | null;
    relationship?: string | null;
}

/**
 * The row patch that block writes onto a witness, built here rather than beside the client so the
 * one rule that makes it safe is testable.
 *
 * `relationship` IS OMITTED WHEN IT IS `undefined`, and an omitted column keeps whatever the row
 * already holds.
 *
 * WHY IT WOULD BE UNDEFINED. Two of that block's three fields come from the board's contract
 * snapshot and one from the witness LIST, which is a separate query and can still be in flight
 * while the block is on screen and editable. In that window the list-backed field is empty because
 * nothing has ARRIVED, not because nothing is recorded — so a Save fired then would write NULL over
 * a relationship somebody entered, silently, with no screen anywhere showing the loss.
 * `witnessContactStatement` below decides whether an edit is stating it; this function is only what
 * that decision is written with.
 *
 * CLEARING STILL WORKS, which is what keeps this from being a one-way door: an admin emptying the
 * field passes an empty STRING, and an empty string is a write of NULL.
 */
export function witnessContactPatch(fields: {
    phone: string;
    address: string;
    relationship?: string;
}): WitnessContactPatch {
    return {
        phone: emptyToNull(fields.phone),
        address: emptyToNull(fields.address),
        ...(fields.relationship === undefined
            ? {}
            : { relationship: emptyToNull(fields.relationship) }),
    };
}

/** What the parties block's commit states about the witness, before it is turned into a patch. */
export interface WitnessContactStatement {
    phone: string;
    address: string;
    /** Absent when nobody has stated anything for it — the column is left alone. */
    relationship?: string;
}

/**
 * Which of the parties block's three fields the commit is actually STATING a value for.
 *
 * THE DRAFT ALONE CANNOT ANSWER THIS, and believing it could is what made the block wipe recorded
 * data. Its list-backed field is seeded `''` while `fetchWitnesses` is in flight, and reducer
 * rule 1 — correctly — keeps that stale empty for as long as the group stays dirty, so an admin who
 * started typing a phone number before the list landed still holds `''` in a relationship the list
 * has since answered `Brother` for. At the commit, `''` and `Brother` differ; the draft looks
 * edited; the write clears a field nobody touched.
 *
 * COMPARING AGAINST THE SERVER DOES NOT ANSWER IT EITHER, which is worth stating because it is the
 * obvious fix. By commit time `state.server` holds the late-arrived `Brother`, so a stale empty
 * differs from it exactly as a deliberate clearing does — the same wipe, one comparison later.
 *
 * WHAT SEPARATES THEM IS WHO PUT THE VALUE THERE. Rule 6's `touched` names the fields an admin
 * typed into, and only a keystroke can put a field in it. So a field they emptied on purpose is
 * stated as `''` and clears the column, a field they never touched is not stated at all, and the
 * list's own timing stops mattering: what is untouched is silent whether the list has answered or
 * not. A value typed BEFORE the list lands is still the admin's and is still written — which the
 * timing-based gate this replaces used to throw away just as silently.
 *
 * THE SNAPSHOT-BACKED TWO ARE ALWAYS STATED. They arrive with the board's contract read, which is
 * the same read that put the block on screen, so there is no window in which they are empty for
 * want of an answer — and clearing a phone typed in error has to reach the write.
 */
export function witnessContactStatement(
    draft: Record<string, string>,
    touched: readonly string[],
): WitnessContactStatement {
    const stated = (field: string): string | undefined =>
        touched.includes(field) ? (draft[field] ?? '') : undefined;

    return {
        phone: draft.witnessPhone ?? '',
        address: draft.witnessAddress ?? '',
        relationship: stated('witnessRelationship'),
    };
}

/**
 * The refusal for an incomplete witness, or null when there is none.
 *
 * A MISSING NAME GETS ITS OWN SENTENCE. It is the one field whose absence is not about what the
 * templates print — there is nothing to call the person — and answering it with the phone-and-
 * address sentence sends an admin to the wrong field.
 */
export function validateWitnessDraft(draft: WitnessDraft): string | null {
    if (!filled(draft.name)) return 'Enter the witness’s full name.';
    if (!filled(draft.phone) || !filled(draft.address)) return WITNESS_CREATE_REFUSAL;
    return null;
}

/** Whether the create control may be enabled at all. Same rule, asked the other way round. */
export function witnessDraftComplete(draft: WitnessDraft): boolean {
    return validateWitnessDraft(draft) === null;
}

/**
 * What each picker row says beside the name: the two printed fields THEMSELVES.
 *
 * THE ROW USED TO REPORT PRESENCE — `phone and address on file` — which spends the whole meta
 * saying that two facts exist without saying what either of them IS. A list of people is
 * disambiguated by their numbers and their streets, not by a note that both were typed; and the
 * hirer line directly above already reads `233540681745 · JMX2+532 Oduman`, so the witness reading
 * `phone and address on file` was the odd one out on its own block.
 *
 * ABSENCE KEEPS ITS OWN WORDS, PART BY PART. A missing field still matters — the witness agreement
 * prints both — so the half that has nothing to show says `phone missing` or `address missing` in
 * the meta's own muted type, in the position the value would have taken.
 *
 * THE ADDRESS TRAILS DELIBERATELY. `.sd-pop-m` ellipses from the end, so the part that gives way
 * on a narrow row is the long unbounded one, and the phone — shorter, and the more identifying of
 * the two — is never the part that gets clipped.
 */
export function witnessMeta(witness: Pick<WitnessOption, 'phone' | 'address'>): string {
    const phone = witness.phone?.trim() || 'phone missing';
    const address = witness.address?.trim() || 'address missing';
    return `${phone} · ${address}`;
}

/**
 * The whole witness as one line — `name · phone · address` — for the row's `title` and the
 * trigger's, whose visible text is the name alone because the trigger is an identity.
 *
 * A ROW IS THE TRUNCATING UNIT, so the tooltip is the row's rather than any one cell's: a pointer
 * that has to find the exact cell that happened to ellipse is a pointer that finds nothing.
 */
export function witnessLine(witness: Pick<WitnessOption, 'name' | 'phone' | 'address'>): string {
    return `${witness.name} · ${witnessMeta(witness)}`;
}

/** Everything a witness row can be found by — the name they are known as, the number they answer. */
export function witnessSearchText(witness: Pick<WitnessOption, 'name' | 'phone'>): string {
    return [witness.name, witness.phone ?? ''].filter(Boolean).join(' ');
}

/**
 * The witnesses a contract may pick from: the ones standing on no contract yet, plus whoever this
 * contract already names.
 *
 * A WITNESS IS NOT EXCLUSIVE IN THE SCHEMA — `contracts.witness_id` points at a standalone table,
 * so one person could stand on several instruments. The office's rule is that they do not, and
 * this filter is that rule; the currently-picked witness is exempt or the row could never show
 * who it holds.
 */
export function availableWitnesses(
    all: WitnessOption[],
    selectedId: number | null,
): WitnessOption[] {
    return all.filter((witness) => witness.id === selectedId || witness.contractCount === 0);
}
